import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { Layout } from './components/layout/Layout';
import { InboxPage } from './components/inbox/InboxPage';
import { CampaignDashboard } from './components/campaigns/CampaignDashboard';
import { ContactsPage } from './components/contacts/ContactsPage';
import { DomainsPage } from './components/domains/DomainsPage';
import { WarmupPage } from './components/warmup/WarmupPage';
import { SniperPage } from './components/sniper/SniperPage';
import { LoginPage } from './components/auth/LoginPage';
import type { DomainConfig, SenderAccount } from './components/layout/AccountSwitcher';

const API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';

// ─── Auth type ─────────────────────────────────────────────
interface AuthInfo {
  token: string;
  role: string;
  regions: string[];  // countryCode values this admin can see
  label: string;
}

// ─── Region label mapping ──────────────────────────────────
const REGION_LABELS: Record<string, string> = {
  LAT: 'Latin America', US: 'North America', EU: 'Europe',
  ASIA: 'Asia-Pacific', AE: 'Middle East', AF: 'Africa',
};

// Map domain TLD → region code (since API doesn't return countryCode)
const DOMAIN_TO_REGION: Record<string, string> = {
  lat: 'LAT', us: 'US', eu: 'EU',
  asia: 'ASIA', ae: 'AE', africa: 'AF',
};

export default function App() {
  const [auth, setAuth] = useState<AuthInfo | null>(null);
  const [dynamicAccounts, setDynamicAccounts] = useState<DomainConfig[]>([]);

  // Load real accounts from /domains API when auth changes
  useEffect(() => {
    if (!auth) { setDynamicAccounts([]); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/domains`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const data = await res.json();
        const loaded: DomainConfig[] = (data.domains || []).map((d: any) => {
          const tld = d.domain.split('.').pop()?.toLowerCase() || '';
          const regionCode = d.countryCode || DOMAIN_TO_REGION[tld] || tld.toUpperCase();
          return {
            countryCode: regionCode,
            country: REGION_LABELS[regionCode] || d.country || d.domain,
            domain: d.domain,
            senders: (d.senders || [])
              .filter((s: any) => s.hasPassword)
              .map((s: any) => ({
                email: s.email,
                domain: d.domain,
                displayName: s.displayName || s.email.split('@')[0],
                dailySendLimit: s.dailySendLimit || 50,
                sentToday: s.sentToday || 0,
              })),
          };
        }).filter((d: DomainConfig) => d.senders.length > 0);
        setDynamicAccounts(loaded);
      } catch (err) {
        console.error('[App] Failed to load domains:', err);
      }
    })();
  }, [auth]);

  // Filter accounts based on region access
  const accounts = auth
    ? dynamicAccounts.filter((a) => auth.regions.map(r => r.toLowerCase()).includes(a.countryCode.toLowerCase()))
    : [];

  const [selectedAccount, setSelectedAccount] = useState<SenderAccount | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);

  // Auto-select first account when accounts load
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccount) {
      setSelectedAccount(accounts[0].senders[0]);
    }
  }, [accounts, selectedAccount]);

  const pollUnread = useCallback(async () => {
    if (!auth) return;
    try {
      const res = await fetch(`${API}/replies/unread`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const data = await res.json();
      setUnreadCount((data.threads || []).length);
    } catch { }
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    pollUnread();
    const t = setInterval(pollUnread, 30000);
    return () => clearInterval(t);
  }, [auth, pollUnread]);

  function handleLogin(authInfo: AuthInfo) {
    setAuth(authInfo);
    setSelectedAccount(null); // will auto-select via useEffect
  }

  function handleLogout() {
    setAuth(null);
    setSelectedAccount(null);
    localStorage.removeItem('acq_auth');
  }

  // ─── Not logged in → show login page ──────────────────────
  if (!auth) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // ─── Logged in → show dashboard ───────────────────────────
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <Layout
            accounts={accounts}
            selectedAccount={selectedAccount}
            onSelectAccount={setSelectedAccount}
            unreadCount={unreadCount}
            composeOpen={composeOpen}
            onComposeOpen={() => setComposeOpen(true)}
            onComposeClose={() => setComposeOpen(false)}
            authLabel={auth.label}
            onLogout={handleLogout}
          />
        }>
          <Route index element={<Navigate to="/campaigns" replace />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="campaigns" element={<CampaignDashboard />} />
          <Route path="contacts" element={<ContactsPage accounts={accounts} />} />
          <Route path="sniper" element={<SniperPage />} />
          <Route path="domains" element={<DomainsPage accounts={accounts} />} />
          <Route path="warmup" element={<WarmupPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}